/* eslint-disable no-console */
import { TipoCalculoRendimiento } from '@/js/enums/production';
import { callExecuteSPERP } from '@/js/services/APIErp';
import qs from 'qs';
import { ApiModule, httpClient } from '../api';

/** @import { AxiosInstance } from 'axios' */
/**
 * @import {
 * InfoDistribucionPlanificacion,
 * TipoArticulo,
 * Respuesta,
 * } from '@erpagroapp/types' */

/**
 * Fetches all active pallets for the given list of palletizing point ids.
 *
 * @param {number} idPuntoDePaletizado - The ID of the palletizing point.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerPedidosPlanificadosPuntoDePaletizado(idPuntoDePaletizado) {
  const apiUrl = `${ApiModule.Traceability}/Paletizado/PedidosPlanificadosPuntoDePaletizado`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: { idPuntoDePaletizado },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Plans a production order in a palletizing point.
 *
 * @param {object} params - The parameters for the API call.
 * @param {number} params.salesOrderLineId - The ID of the order line.
 * @param {number} params.palletizingPointId - The ID of the palletizing point.
 * @param {number} params.productionLineId - The ID of the production line.
 * @param {number} params.quantity - The quantity to be produced.
 * @param {number | null} [params.productionOrderId] - The ID of the production order.
 * @param {number | null} [params.plannedProductionOutputPartId]
 * - The ID of the planned production output part (`ParteProduccionPrevistoSalida`).
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callPlanificarPedidoEnPuntoPaletizado({
  salesOrderLineId,
  palletizingPointId,
  productionLineId,
  quantity,
  productionOrderId = null,
  plannedProductionOutputPartId = null,
}) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/PlanificarPedidoEnPuntoPaletizado`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idLineaPedido: salesOrderLineId,
        idPuntoPaletizado: palletizingPointId,
        idLineaProduccion: productionLineId,
        cantidad: quantity,
        idOrdenProduccion: productionOrderId,
        idParteProduccionPrevistoSalida: plannedProductionOutputPartId,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Deletes a planned production order in a palletizing point.
 *
 * @param {number} IdParteProduccionPrevSalidaPPal - The ID of the previous production part exiting the main line.
 * @param {number} IdPuntoPaletizado - The ID of the palletizing point.
 * @param {number} IdLineaPedidoVenta - The ID of the sales order line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callEliminarPedidoPlanificadoEnPuntoPaletizado(IdParteProduccionPrevSalidaPPal, IdPuntoPaletizado, IdLineaPedidoVenta) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/EliminarPedidoPlanificadoEnPuntoPaletizado`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idParteProduccionPrevSalidaPPal: IdParteProduccionPrevSalidaPPal,
        idPuntoPaletizado: IdPuntoPaletizado,
        idLineaPedidoVenta: IdLineaPedidoVenta,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Initiates costs for a production order.
 *
 * @param {number} IdParteProduccionReal - The ID of the real production part.
 * @param {number} idLineaProduccion - The ID of the production line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callIniciarCostesPedido(IdParteProduccionReal, idLineaProduccion) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/IniciarCostesPedido`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        IdParteProduccionReal,
        IdLineaProduccion: idLineaProduccion,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Stops costs for a production order.
 *
 * @param {number} IdParteProduccionReal - The ID of the real production part.
 * @param {number} IdLineaProduccion - The ID of the production line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callDetenerCostesPedido(IdParteProduccionReal, IdLineaProduccion) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/DetenerCostesPedido`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        IdParteProduccionReal,
        IdLineaProduccion,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Pauses costs for a production order.
 *
 * @param {number} idPartesProduccionReal - The ID of the real production part.
 * @param {number} idLineaProduccion - The ID of the production line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callPausarCostesPedido(idPartesProduccionReal, idLineaProduccion) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/PausarCostesPedido`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idPartesProduccionReal,
        idLineaProduccion,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Resumes costs for a production order.
 *
 * @param {number} idParteProduccionReal - The ID of the real production part.
 * @param {number} idLineaProduccion - The ID of the production line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callReanudarCostesPedido(idParteProduccionReal, idLineaProduccion) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/ReanudarCostesPedido`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idParteProduccionReal,
        idLineaProduccion,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Checks if all pallets and containers for the order line have been reserved for the order.
 *
 * @param {number} idPartesProduccionReal - The ID of the real production parts.
 * @param {number} idLineaPedidoVenta - The ID of the sales order line.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callComprobarSiTodosLosPalesYEnvasesDeLaLineaPedidoHanSidoReservadoAlPedido(idPartesProduccionReal, idLineaPedidoVenta) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/ComprobarSiTodosLosPalesYEnvasesDeLaLineaPedidoHanSidoReservadoAlPedido`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idPartesProduccionReal,
        idLineaPedidoVenta,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Retrieves estimated yields for active sales order lines of a given production line.
 *
 * @param {object} params
 * @param {number} params.productionLineId - The ID of the production line.
 * @param {typeof TipoCalculoRendimiento[keyof typeof TipoCalculoRendimiento]} [params.yieldCalculationType] - The type of yield calculation.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerRendimientosEstimadosLineasPedidosActivosDeXLineaProduccion({
  productionLineId,
  yieldCalculationType = TipoCalculoRendimiento.Estandar,
}) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/RendimientosEstimadosLineasPedidosActivosDeXLineaProduccion`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idLineaProduccion: productionLineId,
        tipoCalculoRendimiento: yieldCalculationType,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Changes the order of planned orders.
 *
 * @param {string} idsPedidosPlanificados - The IDs of the planned orders, separated by commas.
 * @throws {Error} If there is an issue with the API request.
 * @returns {Promise<Respuesta>} A promise that resolves with the response data.
 */
export async function callCambiarOrdenPedidosPlanificados(idsPedidosPlanificados) {
  const apiUrl = `${ApiModule.Traceability}/Paletizado/CambiarOrdenPedidosPlanificados`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: { idsPedidosPlanificados },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Gets data attachments for an entity.
 *
 * @param {number} entityId - The ID of the entity.
 * @param {string} entityType - The type of the entity.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerDatosAdjuntosEntidadSeleccionada(entityId, entityType) {
  const apiUrl = `${ApiModule.Common}/DatosAdjuntosEntidad`;

  try {
    const response = await httpClient().get(apiUrl, {
      params: {
        idEntidad: entityId,
        tipoEntidad: entityType,
      },
    });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Distributes planning by production lines.
 *
 * @param {InfoDistribucionPlanificacion[]} distributionList - The list of distribution data.
 * @returns {Promise<Respuesta>} A promise that resolves with the distributed data.
 */
export async function callDistribuirPlanificacionXLineasProduccion(distributionList) {
  const apiUrl = `${ApiModule.Production}/GestionPedidos/DistribuirPlanificacionXLineasProduccion`;

  try {
    const response = await httpClient().post(apiUrl, distributionList);
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Gets the list of articles.
 * @param {object} params
 * @param {TipoArticulo} [params.tipoArticulo] - The type of the article.
 * @param {number} [params.id] - The ID of the article.
 * @param {string} [params.codigoArticulo] - The code of the article.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerArticulos({
  id = 0,
  codigoArticulo = '',
  tipoArticulo,
} = {}) {
  const apiUrl = `${ApiModule.Traceability}/Paletizado/Articulos`;
  const params = {
    Id: id,
    CodigoArticulo: codigoArticulo,
    TipoArticulo: tipoArticulo,
  };

  try {
    const response = await httpClient().get(apiUrl, { params });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Gets the list of article families.
 * @param {object} params
 * @param {number} [params.id] - The ID of the article.
 * @param {string} [params.codigoFamilia] - The code of the article.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerFamiliasDeArticulos({
  id = 0,
  codigoFamilia = '',
} = {}) {
  const apiUrl = `${ApiModule.Traceability}/Paletizado/FamiliaDeArticulo`;
  const params = {
    Id: id,
    CodigoFamilia: codigoFamilia,
  };

  try {
    const response = await httpClient().get(apiUrl, { params });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

/**
 * Gets the list of article brands.
 * @param {object} params
 * @param {number} [params.id] - The ID of the article.
 * @param {string} [params.codigoMarca] - The code of the article.
 * @returns {Promise<Respuesta>} A promise that resolves with the fetched data.
 */
export async function callObtenerMarcas({
  id = 0,
  codigoMarca = '',
} = {}) {
  const apiUrl = `${ApiModule.Traceability}/Paletizado/Marcas`;
  const params = {
    Id: id,
    CodigoMarca: codigoMarca,
  };

  try {
    const response = await httpClient().get(apiUrl, { params });
    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export default {
  callObtenerLineasDeProduccion() {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/LineasDeProduccionAutorizadas`, { params: {} })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerLineaDeProduccionPorCodigo(codigoLinea) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/LineaDeProduccionAutorizadaPorCodigo`, { params: { codigoLinea } })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPuntosDeVolcado(idLinea) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/PuntosDeVolcado`, {
        params: {
          idLineaProduccion: idLinea,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callSalidaProduccionxLineaProduccionPuntoVolcado(idLinea, tipoElemento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/SalidaProduccionxLineaProduccionPuntoVolcado`, {
        params: {
          idPuntoVolcado: idLinea,
          TipoElementoProduccion: tipoElemento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarPuntoVolcadoElementoProduccion(lstPVElementosProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().post(
        `${ApiModule.Traceability}/Volcado/GuardarPuntosVolcadoElementoProduccion`,
        lstPVElementosProduccion,
      )
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  // callObtenerPuntoDeVolcadoPorCodigo(codigoPunto) {
  //
  //   return new Promise((resolve, reject) => {
  //     httpClient().get(`${ApiModule.Traceability}/Volcado/PuntoDeVolcadoAutorizadoPorCodigo`, {
  //       params: {
  //         codigoPuntoVolcado: codigoPunto,
  //       },
  //     })
  //       .then((response) => {
  //         const objJson = response.data;
  //         resolve(objJson);
  //       })
  //       .catch((error) => {
  //         console.log(error);
  //         reject(error);
  //       })
  //   });
  // },
  callObteneElementoDeProduccionPorIdentificadorNumeroSerie(identificadorNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ArticuloNumeroSerieXNumeroSerie`, {
        params: {
          numeroSerie: identificadorNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObteneElementoDeProduccionPorIdArticuloNumeroSerie(idArticuloNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ArticuloNumeroSerieXId`, {
        params: {
          idArticuloNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callVolcarTodaLaMercanciaArticuloNumeroSerie(idLineaProduccion, idPuntoVolcado, idArticuloNumeroSerie, idProceso) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/VolcarTodaLaMercanciaArticuloNumeroSerie`, {
        params: {
          idLineaProduccion,
          idPuntoVolcado,
          idArticuloNumeroSerie,
          idProceso,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObteneElementoDeProduccionPorCodigoPartida(codigoPartida, obtenerExistencias) {
    let cargarExistencias = obtenerExistencias;
    if (obtenerExistencias === undefined) {
      cargarExistencias = true;
    }
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementoDeProduccionPorCodigoPartida`, {
        params: {
          codigoPartida,
          obtenerExistencias: cargarExistencias,
        },
      })
        .then((response) => {
          const objJson = response.data;
          console.log(objJson);
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObteneElementoDeProduccionPorIdPartida(idPartida, obtenerExistencias) {
    let cargarExistencias = obtenerExistencias;
    if (cargarExistencias === undefined) {
      cargarExistencias = true;
    }

    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementoDeProduccionPorIdPartida`, {
        params: {
          idPartida,
          obtenerExistencias: cargarExistencias,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObteneElementoDeProduccionPorCodigoDeBarras(codigoDeBarras) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementoDeProduccionPorCodigoDeBarras`, {
        params: {
          codigoDeBarras,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObteneElementoDeProduccionPorCodigoTag(codigoTag) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementoDeProduccionPorCodigoTag`, {
        params: {
          codigoTag,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerColaDeVolcadoPorIdPuntoVolcado(idPuntoVolcado, numeroDeVolcadosMostrar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ColaDeVolcadoPorIdPuntoVolcado`, {
        params: {
          idPuntoVolcado,
          cantidad: numeroDeVolcadosMostrar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerLotesVolcadosParaXPuntoVolcado(idPuntoVolcado, fechaJornada, obtenerColasVolcadoRelacionadas) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ObtenerLotesVolcadosParaXPuntoVolcado`, {
        params: {
          idPuntoVolcado,
          fechaJornada,
          obtenerColasVolcadoRelacionadas,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPartidasActivasParaPrecalibradoPorPuntoDePaletizado(idPuntoPaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PartidasActivasParaPrecalibradoPorPuntoDePaletizado`, {
        params: {
          idPuntoPaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerResumenFamiliasMercanciaVolcada(idPuntoVolcado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ResumenDeVolcadosPorFamilia`, {
        params: {
          idPuntoVolcado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callRecuperarMercanciaVolcada(idLineaProduccion, idPuntoVolcado, idPartida, numeroSerie, idColaVolcado, numeroDeCajasRecuperar, cantidadKilosRecuperar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/RecuperarMercanciaVolcada`, {
        params: {
          idLineaProduccion,
          idPuntoVolcado,
          idPartida,
          numeroSerie,
          idColaVolcado,
          numeroDeCajasRecuperar,
          cantidadKilosRecuperar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callRecuperarMercanciaVolcadaDeUnidadLogistica(idLineaProduccion, idPuntoVolcado, numeroSerie, listObjEstructuraRecuperacion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/RecuperarMercanciaVolcadaArticuloNumeroSerie`, {
        params: {
          idLineaProduccion,
          idPuntoVolcado,
          numeroSerie,
          listObjEstructuraRecuperacion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerElementosRecuperablesDeUnaPartida(idPuntoVolcado, idPartida, numeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementosRecuperablesDeUnaPartida`, {
        params: {
          idPuntoVolcado,
          idPartida,
          numeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerElementosRecuperablesArticuloNumeroSerie(idPuntoVolcado, idNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ElementosRecuperablesArticuloNumeroSerie`, {
        params: {
          idPuntoVolcado,
          idNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callVolcarMercancia(idLineaProduccion, idPuntoVolcado, idPartida, idUnidadLogistica, cantidadEnvases) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/VolcarMercancia`, {
        params: {
          idLineaProduccion,
          idPuntoVolcado,
          idPartida,
          idUnidadLogistica,
          cantidadEnvases,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerVolcadosDeUnaFamilia(idPuntoVolcado, idFamilia) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/VolcadosDeUnaFamilia`, {
        params: {
          idPuntoVolcado,
          idFamilia,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPuntosDePaletizado(idLinea) {
    return new Promise((resolve, reject) => {
      // httpClient().get(conexion.getHost()+ '/' +'ObtenerPuntosDePaletizado', {
      httpClient().get(`${ApiModule.Traceability}/PuntosDePaletizadoAutorizados`, {
        params: {
          idLineaProduccion: idLinea,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPuntoDePaletizadoPorCodigo(codigoPunto) {
    return new Promise((resolve, reject) => {
      // httpClient().get(conexion.getHost()+ '/' +'ObtenerPuntoDePaletizadoPorCodigo', {
      httpClient().get(`${ApiModule.Traceability}/PuntoDePaletizadoAutorizadoPorCodigo`, {
        params: {
          codigoPuntoPaletizado: codigoPunto,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticulosMateriaPrimaDeUnaFamilia(idFamiliaArticulo) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ArticulosMateriaPrimaDeUnaFamilia`, {
        params: {
          idFamiliaArticulo,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticulosDeTipoEnvasePorCodigo(codigoArticuloEnvase) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/ArticuloDeTipoEnvasePorCodigo`, {
        params: {
          codigoArticuloEnvase,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callConfirmarPalePrecalibrado(idPuntoPaletizado, idLineaProduccion, idUnidadLogistica, idEnvase, idPartidaEntrada, idPale, idProducto, numCajas, pesoNetoNormalizado, confirmadaUbicacionPorDefecto) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ConfirmarPalePrecalibrado`, {
        params: {
          idPuntoPaletizado,
          idLineaProduccion,
          idUnidadLogistica, // Si es 0 Genera un nuevo palé
          idEnvase,
          idPartidaEntrada,
          idPale,
          idProducto,
          numCajas,
          pesoNetoNormalizado,
          confirmadaUbicacionPorDefecto,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimasConfeccionesLineaDeProduccion(idLineaProduccion, numeroDeElementosMostrar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/UltimasConfeccionesLineaDeProduccion`, {
        params: {
          idLineaProduccion,
          numeroDeElementosMostrar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerTodasLasCantidadesNormalizadasPorEnvaseYProducto() {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/TodasLasCantidadesNormalizadasPorEnvaseYProducto`, {
        params: {
          usuario: '',
          password: '',

        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerExistenciasDeUnPale(idArticuloNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ExistenciasDeUnPale`, {
        params: {
          idArticuloNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenePalePorCodigoDeBarras(codigoBarrasPale, cargarExistencias, cargarDatosPedidoVentaDelPale, cargarArticulosElaborados) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ObtenePalePorCodigoDeBarras`, {
        params: {
          codigoBarrasPale,
          cargarExistencias,
          cargarDatosPedidoVentaDelPale,
          cargarArticulosElaborados,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenePalePorNumeroDeSerie(numeroDeserie, cargarExistencias, cargarDatosPedidoVentaDelPale, cargarArticulosElaborados) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PalePorNumeroDeSerie`, {
        params: {
          numeroDeserie,
          cargarExistencias,
          cargarDatosPedidoVentaDelPale,
          cargarArticulosElaborados,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarExistenciaDeUnPale(idArticuloNumeroSerie, idPartida) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/EliminarExistenciaDeUnPale`, {
        params: {
          idArticuloNumeroSerie,
          idPartida,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callMofificarEnvasesYKilosPartidaDePale(idLineaProduccion, idPuntoPaletizado, idArticuloNumeroSerie, idPartidaEntrada, idPartidaSalida, cantidadEnvases, cantidadKilos) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/MofificarEnvasesYKilosPartidaDePale`, {
        params: {
          idLineaProduccion,
          idPuntoPaletizado,
          idArticuloNumeroSerie,
          idPartidaEntrada,
          idPartidaSalida,
          cantidadEnvases,
          cantidadKilos,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callImprimirEtiquetaPale(idUnidadLogistica, idPuntoDePaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ImprimirEtiquetaPaleParaProcesoDePaletizado`, {
        params: {
          idUnidadLogistica,
          idPuntoDePaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callImprimirEtiquetaPartidaDePale(idPartida, idUnidadLogistica, idPuntoPaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ImprimirEtiquetaPartidaDePale`, {
        params: {
          idPartida,
          idUnidadLogistica,
          idPuntoPaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPedidoPlanificadoPuntoDePaletizado(ordenPedido, idPuntoDePaletizado, modoBusqueda) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PedidoPlanificadoPorOrdenDeUnPuntoDePaletizado`, {
        params: {
          ordenPedido,
          idPuntoDePaletizado,
          modoBusqueda,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callIniciarPale(idPuntoPaletizado, idLineaProduccion, idLineaPedidoVenta, verificarPalesPlanificadosReservados) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/IniciarPale`, {
        params: {
          idPuntoPaletizado,
          idLineaProduccion,
          idLineaPedidoVenta,
          verificarPalesPlanificadosReservados,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarPale(idPuntoPaletizado, idLineaProduccion, tipoDeFinAtendiendoAPlanificacion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FinalizarPale`, {
        params: {
          idPuntoPaletizado,
          idLineaProduccion,
          tipoDeFinAtendiendoAPlanificacion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarPaleEnUbicacion(idPuntoPaletizado, idLineaProduccion, tipoDeFinAtendiendoAPlanificacion, idUbicacionDestinoPale, imprimirEtiqueta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FinalizarPaleEnUbicacion`, {
        params: {
          idPuntoPaletizado,
          idLineaProduccion,
          tipoDeFinAtendiendoAPlanificacion,
          idUbicacionDestinoPale,
          imprimirEtiqueta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEnvasesAsignadosAlPaleEnCursoPorPuntoPaletizado(palletizingPointId) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/IndividualProduction/ManufacturedElementsAssignedToThePalletInProgress`, {
        params: {
          palletizingPointId,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callAsignarTagNFCPorPale(tag, idNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/AsignarTagNFCPorPale`, {
        params: {
          tag,
          idNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callCambiarTipoEnvasadoXPuntoPaletizado(idPuntoPaletizado, idArticuloTipoPale, idArticuloTipoEnvase, idArticuloProducto, idMarca, cantidadEnvasesPorPalet) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/CambiarTipoEnvasadoXPuntoPaletizado`, {
        params: {
          idPuntoPaletizado,
          idArticuloTipoPale,
          idArticuloTipoEnvase,
          idArticuloProducto,
          idMarca,
          cantidadEnvasesPorPalet,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  callFinalizarFabricacionLineaPedido(idPartesProduccionPrevistoSalidaPuntoPaletizado, idPuntoPaletizado, idLineaPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FinalizarFabricacionLineaPedido`, {
        params: {
          idPartesProduccionPrevistoSalidaPuntoPaletizado,
          idPuntoPaletizado,
          idLineaPedidoVenta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  callFinalizarPaleConLecturaUltimoBulto(idPuntoPaletizado, codigoBarrasArticuloElaborado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FinalizarPaleConLecturaUltimoBulto`, {
        params: {
          idPuntoPaletizado,
          codigoBarrasArticuloElaborado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticulosTipoProducto() {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ArticulosTipoProducto`)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticulosDeTipoProductoPorCodigo(codigo) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/ArticulosTipoProductoPorCodigo`, {
        params: {
          codigoArticuloProducto: codigo,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUbicaciones() {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Warehouse}/Ubicaciones/Ubicaciones`)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUbicacionPorCodigo(codigoUbicacion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Warehouse}/Ubicaciones/UbicacionPorCodigo`, {
        params: {
          codigo: codigoUbicacion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callMovimientoMercanciaCambioUbicacion(selectedAccumulated, IdLocationDestination, idContentUnitDestination) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Warehouse}/Ubicaciones/MovementMerchandiseChangeLocation`, selectedAccumulated, {
        params: {
          IdLocationDestination,
          idContentUnitDestination,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callMovimientoMercanciaConNuevoPale(idNewPaleDestination, IdLocationDestination, selectedAccumulated) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Warehouse}/Ubicaciones/MovementMerchandiseChangeLocationWithNewLogisticUnit`, selectedAccumulated, {
        params: {
          IdLocationDestination,
          idNewPaleDestination,
        },
      })
        .then((response) => {
          const objJson = response.data;

          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callCambiarUbicacion(idsArticulosNumerosSerie, idUbicacionDestino) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Warehouse}/Ubicaciones/CambiarUbicacion`, {
        params: {
          idsArticulosNumerosSerie,
          idUbicacionDestino,
        },
      })
        .then((response) => {
          const objJson = response.data;

          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callSincronizarMovimientosCambioUbicacion(listaMovimientosPendientes) {
    return new Promise((resolve, reject) => {
      httpClient().post(
        `${ApiModule.Warehouse}/Ubicaciones/SincronizarMovimientosCambioUbicacion`,
        listaMovimientosPendientes,
      )
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPartidasRecientesDeLaColaDeVolcado(idLineaProduccion, cantidadPartidasFinalizadasRecuperar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/PartidasRecientesDeLaColaDeVolcado`, {
        params: {
          idLineaProduccion,
          cantidadPartidasFinalizadasRecuperar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPartidasRecientesDeLaColaDeVolcadoXPuntoPaletizado(idPuntoPaletizado, cantidadPartidasFinalizadasRecuperar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/PartidasRecientesDeLaColaDeVolcadoXPuntoPaletizado`, {
        params: {
          idPuntoPaletizado,
          cantidadPartidasFinalizadasRecuperar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimosPalesGeneradosEnLineaOPuntoDePaletizado(numeroDiasMostrar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/UltimosPalesGeneradosEnLineaOPuntoDePaletizado`, {
        params: {
          numeroDiasMostrar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPedidosPendientesPlanificacionXLineaProduccion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/PedidosPendientesPlanificacionXLineaProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGenerarPaleSinAsociarAPedidoPlanificado(idPuntoPaletizado, idLineaProduccion, idArticuloTipoPale, idArticuloTipoEnvase, cantidadEnvasesPorPalet, idMarca, idArticuloProducto) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/GenerarPaleSinAsociarAPedidoPlanificado`, {
        params: {
          idPuntoPaletizado,
          idLineaProduccion,
          idArticuloTipoPale,
          idArticuloTipoEnvase,
          cantidadEnvasesPorPalet,
          idMarca,
          idArticuloProducto,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callModificarDatosEnvasadoEnElementoDeProduccion(idElemento, tipoElemento, idArticuloTipoPale, idArticuloTipoEnvase, idArticuloProducto, idMarca, cantidadEnvasesPorPalet) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/ModificarDatosEnvasadoEnElementoDeProduccion`, null, {
        params: {
          idElemento,
          tipoElemento,
          idArticuloTipoPale,
          idArticuloTipoEnvase,
          idArticuloProducto,
          idMarca,
          cantidadEnvasesPorPalet,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerMarcaPorCodigo(codigoMarca) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/MarcaPorCodigo`, {
        params: {
          codigoMarca,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPaleActivoSinPedidoPlanificado(idPuntoDePaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PaleActivoSinPedidoPlanificado`, {
        params: {
          idPuntoDePaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callImprimirEtiquetaPaleParaProcesoDePaletizado(idNumeroSeriePale, idPuntoDePaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ImprimirEtiquetaPaleParaProcesoDePaletizado`, {
        params: {
          idNumeroSeriePale,
          idPuntoDePaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerFamiliasDeArticulosRecoleccion(Id, CodigoFamilia, IdFinca) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FamiliaDeArticuloRecoleccionXLineaProduccion`, {
        params: {
          Id,
          CodigoFamilia,
          IdFinca,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerEnvasesParaRecoleccion(Id, CodigoArticulo) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/EnvasesParaRecoleccion`, {
        params: {
          Id,
          CodigoArticulo,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPaletsParaRecoleccion(Id, CodigoArticulo) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PaletsParaRecoleccion`, {
        params: {
          Id,
          CodigoArticulo,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerFamiliaDeArticulosPorCodigo(codigoFamilia) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/FamiliaDeArticulosPorCodigo`, {
        params: {
          codigoFamilia,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPalesActivosEnLineaProduccion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PalesActivosEnLineaProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerIncidenciasPlanificacion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/IncidenciasPlanificacion`, {
        params: {
          idLineaProduccion,
        },
      })

        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarPaleConInformacionDeExistencias(idPuntoPaletizado, idArticuloTipoPale, idPedidoVenta, idLineaPedido, existenciasDelPale, verificarReservaDePalesAntesDeGeneraNuevoPaleDePedido) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/FinalizarPaleConInformacionDeExistencias`, existenciasDelPale, {
        params: {
          idPuntoPaletizado,
          idArticuloTipoPale,
          idPedidoVenta,
          idLineaPedidoVenta: idLineaPedido,
          verificarReservaDePalesAntesDeGeneraNuevoPaleDePedido,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarPaleRetrabajoConInformacionDeExistencias(idPuntoPaletizado, idAnsRetrabajado, existenciasDelPale, modoRetrabajoPorKilos) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/FinalizarPaleRetrabajoConInformacionDeExistencias`, existenciasDelPale, {
        params: {
          idPuntoPaletizado,
          idAnsRetrabajado,
          modoRetrabajoPorKilos,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimasPartidasVolcadasConInformacionDeKilosDisponibles(idLineaProduccion, cantidadPartidasFinalizadasRecuperar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/UltimasPartidasVolcadasConInformacionDeKilosDisponibles`, {
        params: {
          idLineaProduccion,
          cantidadPartidasFinalizadasRecuperar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimasPartidasVolcadasXPuntoPaletizadoConInformacionDeKilosDisponibles(idPuntoPaletizado, cantidadPartidasFinalizadasRecuperar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/UltimasPartidasVolcadasXPuntoPaletizadoConInformacionDeKilosDisponibles`, {
        params: {
          idPuntoPaletizado,
          cantidadPartidasFinalizadasRecuperar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerPedidosPlanificadosLineaDeProduccion(idLineaProduccion) {
    return this.callObtenerPedidosPendientesPlanificacionXLineaProduccion(idLineaProduccion);
  },

  callObtenerPedidosPlanificadosLineaProduccion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/PedidosPlanificadosLineaProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callObtenerPedidosCompletosLineaProduccion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/PedidosCompletadosLineaProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callObtenerPedidosCompletosLineaProduccionXJornada(idLineaProduccion, jornada) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/PedidosCompletadosLineaProduccionXJornada`, {
        params: {
          idLineaProduccion,
          jornada,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callImprimirEtiquetaIncluirPartidaEnPaleDePuntoPaletizado(idPuntoPaletizado, idPartida, idEnvase, cantidadKilos, cantidadEnvases) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ImprimirEtiquetaIncluirPartidaEnPaleDePuntoPaletizado`, {
        params: {
          idPuntoPaletizado,
          idPartida,
          idEnvase,
          cantidadKilos,
          cantidadEnvases,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callObtenerPlantillasDeTipoEnvasadoParaPuntosDePaletizado(idFamiliaArticulo, idsPuntosPaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/PlantillasDeTipoEnvasadoParaPuntosDePaletizado`, {
        params: {
          usuario: '',
          password: '',
          idFamiliaArticulo,
          idsPuntosPaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callObtenerLineaPorDefectoDefinidaDelUsuario() {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/LineaPorDefectoDefinidaDelUsuario`)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callObtenerUltimosPalesConfeccionadosYEnConfeccion(idPuntoPaletizado, numeroRegistrosMostrar) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/UltimosPalesConfeccionadosYEnConfeccion`, {
        params: {
          idPuntoPaletizado,
          numeroRegistrosMostrar,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  callEliminarFechaFinCostesPedido(IdParteProduccionReal) {
    return new Promise((resolve, reject) => {
      httpClient().delete(`${ApiModule.Production}/GestionPedidos/EliminarFechaFinCostesPedido`, {
        params: {
          IdParteProduccionReal,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callMoverPalePuntoPaletizadoParaRetrabajarlo(idAnsOrigen, idPPDestino) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/MoverPalePuntoPaletizadoParaRetrabajarlo`, {
        params: {
          idAnsOrigen,
          idPPDestino,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerExistenciasDeUnPaleParaRetrabajo(idArticuloNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ExistenciasDeUnPaleParaRetrabajo`, {
        params: {
          idArticuloNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },
  /*
  // Esto dejo de utilizarse por los problemas que ocasiona una desplanificación automatica desde la tablet.
  callDesplanificacionAutomaticaDePedidosConReservaOModificacionDePedido(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/DesplanificacionAutomaticaDePedidosConReservaOModificacionDePedido`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        })
    });
  },
  */
  callFinalizarPartidasActivasEnLaLineaDeProduccion(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/FinalizarPartidasActivasEnLaLineaDeProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarPartidasActivasPuntoVolcado(idsPuntosVolcado) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Volcado/FinalizarPartidasActivasPuntoVolcado`, null, {
        params: {
          idsPuntosVolcado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callFinalizarLotesActivosPuntoVolcado(idsPuntosVolcado) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Volcado/FinalizarLotesActivosPuntoVolcado`, null, {
        params: {
          idsPuntosVolcado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarPaleEnCursoEnPuntoPaletizado(idPuntoPaletizado, idLineaPedido, idPartesProduccionPrevistoSalidaPuntoPaletizado, idOrdenProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/EliminarPaleEnCursoEnPuntoPaletizado`, {
        params: {
          idPuntoPaletizado,
          idLineaPedido,
          idPartesProduccionPrevistoSalidaPuntoPaletizado,
          idOrdenProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerDatosPartidasOrigenDeUnaUnidadLogistica(idUnidadLogistica) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/DatosPartidasOrigenDeUnaUnidadLogistica`, {
        params: {
          idUnidadLogistica,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callActualizarCantidadDePalesPlanificadosConLaCantidadActualPendienteDelPedido(idPuntoPaletizado, idLineaPedidoVenta, idParteProduccionPrevistoSalidaPuntoPaletizado, idOrdenProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/ActualizarCantidadDePalesPlanificadosConLaCantidadActualPendienteDelPedido`, {
        params: {
          idPuntoPaletizado,
          idLineaPedidoVenta,
          idParteProduccionPrevistoSalidaPuntoPaletizado,
          idOrdenProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerDatosDeTrabajadoresQueIntervienenEnLineaProduccion(idLineaProduccion, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/DatosDeTrabajadoresQueIntervienenEnLineaProduccion`, {
        params: {
          idLineaProduccion,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callAsignarPalesDeLineaPedidoEnOtraLineaDeProduccion(idParteProduccion, idParteProduccionPrevistoSalida, cantidadPalesCambiar, idNuevaLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/AsignarPalesDeLineaPedidoEnOtraLineaDeProduccion`, {
        params: {
          idParteProduccion,
          idParteProduccionPrevistoSalida,
          cantidadPalesCambiar,
          idNuevaLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarNumeroTrabajadoresLinea(idLineaProduccion, numeroTrabajadores, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/GuardarNumeroTrabajadoresLinea`, {
        params: {
          idLineaProduccion,
          numeroTrabajadores,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarNumeroTrabajadoresLineaPedidoVentaProduccion(idLineaProduccion, idLineaPedidoVenta, idParteProduccion, numeroTrabajadores, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/GuardarNumeroTrabajadoresLineaPedidoVentaProduccion`, {
        params: {
          idLineaProduccion,
          idLineaPedidoVenta,
          idParteProduccion,
          numeroTrabajadores,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerNumeroDeTrabajadoresQueIntervienenEnLineaProduccion(idLineaProduccion, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/NumeroDeTrabajadoresQueIntervienenEnLineaProduccion`, {
        params: {
          idLineaProduccion,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimosIntervalosLineaProduccionNumeroTrabajadores(idLineaProduccion, numeroIntervalosConsulta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/UltimosIntervalosLineaProduccionNumeroTrabajadores`, {
        params: {
          idLineaProduccion,
          numeroIntervalosConsulta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerIntervalosNumeroTrabajadoresLineaPedidoVentaProduccion(idLineaProduccion, idLineaPedidoVenta, idParteProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/IntervalosNumeroTrabajadoresLineaPedidoVentaProduccion`, {
        params: {
          idLineaProduccion,
          idLineaPedidoVenta,
          idParteProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callCrearOrdenYPartePorLineaPedido(idLineaProduccion, idsLineaPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/GetOrderAndProductionPartByOrderLine`, {
        params: {
          idsLineaPedidoVenta,
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticuloElaboradoXCodigo(codigoArticuloElaborado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/IndividualProduction/ElaboratedItemXCode`, {
        params: {
          code: codigoArticuloElaborado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callUpdatePalletWeight(logisticUnitId, grossWeight) {
    const params = {
      logisticUnitId,
      grossWeight,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/PalletWeighing/UpdatePalletWeight`, null, { params })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callPrintPalletLabel(logisticUnitId) {
    const params = {
      logisticUnitId,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/PalletWeighing/PrintPalletLabel`, null, { params })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerEnvasePorDefectoDestrioXFamilia(idFamilia) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/EnvasePorDefectoDestrioXFamilia`, {
        params: {
          idFamilia,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callRegistrarDestrio(idColaVolcado, idEnvase, cantidadEnvases, cantidadProducto) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/RegistrarDestrio`, {
        params: {
          idColaVolcado,
          idEnvase,
          cantidadEnvases,
          cantidadProducto,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerNumeroDeTrabajadoresLineaPedidoVentaProduccion(idLineaProduccion, idLineaPedidoVenta, idParteProduccion, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/NumeroDeTrabajadoresLineaPedidoVentaProduccion`, {
        params: {
          idLineaProduccion,
          idLineaPedidoVenta,
          idParteProduccion,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarIntervaloFuerzaTrabajo(idLineaProduccion, idLineaPedidoVenta, idParteProduccion, momento) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/EliminarIntervaloFuerzaTrabajo`, {
        params: {
          idLineaProduccion,
          idLineaPedidoVenta,
          idParteProduccion,
          momento,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarPaleConfeccionado(idArticuloNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/EliminarPaleConfeccionado`, {
        params: {
          idArticuloNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerArticulosElaboradosXNumeroSerie(itemSerialNumberId) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/IndividualProduction/ManufacturedItemsXSerialNumber`, {
        params: {
          itemSerialNumberId,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callAsociarArticuloElaboradoConPale(idItemSerialNumber, updateDataOrigin, lstManufacturedItemCodes) {
    const params = {
      idItemSerialNumber,
      updateDataOrigin,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/IndividualProduction/AssociateAElementsManufacturedWithAPallet`, lstManufacturedItemCodes, { params })
      // httpClient().get(`${ApiModule.Production}/ProduccionIndividual/AsociarArticuloElaboradoConPale`, {
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarArticuloElaboradoRegistroActividad(lstActivityRegistryCodes) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/IndividualProduction/AssociateArticleManufacturedWithActivityRegistry`, lstActivityRegistryCodes)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarArticulosElaboradosRegistrosActividad(idItemSerialNumber, lstElementsManufactured) {
    const params = {
      idItemSerialNumber,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/IndividualProduction/AssociateArticlesManufacturedWithActivitysRegistry`, lstElementsManufactured, { params })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarAsociacionArticuloElaboradoConPaleActulizandoDatosOrigen(manufacturedArticleCode) {
    return new Promise((resolve, reject) => {
      httpClient().delete(`${ApiModule.Production}/IndividualProduction/RemoveElementAssociationManufacturedFromAPalletUpdateDataOrigen`, {
        params: {
          manufacturedArticleCode,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarAsociacionArticuloElaboradoConPale(manufacturedArticleCode) {
    return new Promise((resolve, reject) => {
      httpClient().delete(`${ApiModule.Production}/IndividualProduction/RemoveElementAssociationManufacturedFromAPallet`, {
        params: {
          manufacturedArticleCode,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarRegistroActividadArticuloElaborado(idRegistroActividad) {
    return new Promise((resolve, reject) => {
      httpClient().delete(`${ApiModule.Production}/IndividualProduction/RemoveElementAssociationManufacturedFromAActivityRegistry`, {
        params: {
          idRegistroActividad,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerDatosAuxiliaresXLineaPedidoVenta(idLineaPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/DatosAuxiliaresXLineaPedidoVenta`, {
        params: {
          idLineaPedidoVenta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callVolcarPartidaCompleta(idPuntoVolcado, idPartida) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/VolcarPartidaCompleta`, {
        params: {
          idPuntoVolcado,
          idPartida,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerLineasPedidoVentaParaPicking(arrFilter) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/LineasPedidoVentaParaPicking`, {
        params: {
          filtros: arrFilter,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerUltimosPalesTraspasadosReprocesadosSinPedido(usuario, fechaJornada) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/UltimosPalesTraspasadosReprocesadosSinPedido`, {
        params: {
          usuario,
          fechaJornada,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callReservasLineasPedidoVentaParaPicking(idLineaPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/ReservasLineasPedidoVentaParaPicking`, {
        params: {
          idLineaPedidoVenta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGuardarTraspasosYReprocesadosPicking(replanQuantity, infoPicking) {
    const params = {
      replanQuantity,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Picking}/GuardarTraspasosYReprocesadosPicking`, infoPicking, { params })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
      /* httpClient().post(`${ApiModule.Picking}/GuardarTraspasosYReprocesadosPicking`, qs.stringify({
        params: {
          infoPicking: JSON.stringify(infoPicking),
        },
      }))
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        }) */
    });
  },

  callEliminarReservaDePaleLineaPedidoParaPicking(listaULReservadas, deshacerTraspasos) {
    const params = {
      deshacerTraspasos,
    };

    // return httpClient().delete(`${ApiModule.Picking}/EliminarReservaDePaleLineaPedidoParaPicking`, { params});
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Picking}/EliminarReservaDePaleLineaPedidoParaPicking`, listaULReservadas, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callEliminarMovimientosAlmacenPicking(listaIdMovimientosAlmacen, idUlOrigen) {
    const params = {
      idUlOrigen,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Picking}/EliminarMovimientosAlmacenPicking`, listaIdMovimientosAlmacen, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callAsociarLineasPedidoVentaVolcadoXUnidadLogistica(idPuntoVolcado, idUnidadLogistica, listaLineasPedidoVenta) {
    const params = {
      idPuntoVolcado,
      idUnidadLogistica,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Volcado/AsociarLineasPedidoVentaVolcadoXUnidadLogistica`, listaLineasPedidoVenta, { params })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callAsociarVolcadosXLineasPedidoVenta(listaLineasPedidoVenta, listaColasVolcado) {
    const data = {
      LineasPedidoVenta: listaLineasPedidoVenta,
      ColasVolcado: listaColasVolcado,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Volcado/AsociarVolcadosXLineasPedidoVenta`, data)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callReservarPalesLineaPedido(idLineaPeidoVenta, listaPales) {
    const params = {
      idLineaPeidoVenta,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Picking}/ReservarPalesLineaPedido`, listaPales, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
    /* httpClient().post(`${ApiModule.Picking}/ReservarPalesLineaPedido`, qs.stringify({
        params: {
          idLineaPeidoVenta,
          listaPalesReservar: JSON.stringify(listaPales),
        },
      }))
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        })
    }); */
  },

  callImprimirEtiquetaPalePicking(idPale) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/ImprimirEtiquetaPalePicking`, {
        params: {
          idPale,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callImprimirEtiquetaPalePickingSinPedido(idPale) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/ImprimirEtiquetaPalePickingSinPedido`, {
        params: {
          idPale,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callConsultarEstadoULParaUsoPicking(idAns) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Picking}/ConsultarEstadoULPicking`, {
        params: {
          idAns,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerDatosComplementariosEntidadSeleccionada(idEntidad, tipoEntidad, cargarListaValoresParaSeleccion) {
    console.log(`callObtenerDatosComplementariosEntidadSeleccionada: idEntidad: ${idEntidad} , ${tipoEntidad}`);

    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Global}/DatosComplementariosEntidad`, {
        params: {
          idEntidad,
          tipoEntidad,
          cargarListaValoresParaSeleccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callImprimirRecuperacionVolcadoUL(idUnidadLogistica, idPuntoVolcado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ImprimirRecuperacionVolcadoUL`, {
        params: {
          idUnidadLogistica,
          idPuntoVolcado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGestionarIncidenciasPlanificacionLineaPedidoVenta(lstResolucion) {
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Production}/GestionPedidos/GestionarIncidenciasPlanificacionLineaPedidoVenta`, lstResolucion)
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
    /* httpClient().post(`${ApiModule.Production}/GestionPedidos/GestionarIncidenciasPlanificacionLineaPedidoVenta`, {
        params: {
          lstResolucion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        })
    }); */
  },

  callCalcularDatosOrigenPorPedidoFinPale(idPuntoPaletizado, idLineaPedido) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/CalcularDatosOrigenPorPedidoFinPale`, {
        params: {
          idPuntoPaletizado,
          idLineaPedido,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callDatosOrigenPuntoPaletizadoRegistroActivoTiempos(idPuntoPaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/DatosOrigenPuntoPaletizadoRegistroActivoTiempos`, {
        params: {
          idPuntoPaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callConsultaPartidasVolcadasSegunConsumo(idPuntoPaletizado, fechaHoraJornada) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ConsultaPartidasVolcadasSegunConsumo`, {
        params: {
          idPuntoPaletizado,
          fechaHoraJornada,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGestionarFueraUsoColaVolcado(idPuntoVolcado, idPartida, fueraUso, listaLineasPedidoVenta) {
    const params = {
      idPuntoVolcado,
      idPartida,
      fueraUso,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/GestionarFueraUsoColaVolcado`, listaLineasPedidoVenta, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callComprobarCompatibilidadVolcadoAPedido(idPartida, listaLineasPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ComprobarCompatibilidadVolcadoAPedido`, qs.stringify({
        params: {
          idPartida,
          listaLineasPedidoVenta: JSON.stringify(listaLineasPedidoVenta),
        },
      }))
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callComprobarExistenciaPartidasAnterioresParaVolcado(idPartida, idArticuloNumeroSerie) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ComprobarExistenciaPartidasFechaPrevia`, {
        params: {
          idPartida,
          idArticuloNumeroSerie,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callObtenerCapacidadEnvaseYProducto(idProducto, idEnvase, idLineaPedidoVenta) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ObtenerCapacidadEnvaseYProducto`, {
        params: {
          idProducto,
          idEnvase,
          idLineaPedidoVenta,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callPlanificacionesEnPuntosPaletizadoXLinea(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Production}/GestionPedidos/PlanificacionesEnPuntoPaletizadoXLinea`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callProductionOutletsXLine(idLineaProduccion) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ObtenerSalidasProduccionXLineaProduccion`, {
        params: {
          idLineaProduccion,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callDumpLotsXPalletizingPoints(idPuntoPaletizado, fechaJornada, obtenerColasVolcadoRelacionadas) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Volcado/ObtenerLotesVolcadosParaXPuntoPaletizado`, {
        params: {
          idPuntoPaletizado,
          fechaJornada,
          obtenerColasVolcadoRelacionadas,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callProductionLineUser(usuarioLineaProduccionJSON) {
    return httpClient().post(`${ApiModule.Production}/ProductionLineUser`, usuarioLineaProduccionJSON);
  },

  callUpdateProductionStatusXProductionOutputs(outputsList, accion) {
    const params = {
      accion,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/ActualizarSalidaProduccion`, outputsList, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callGetPlanningQuantitiesProducedXPalletizingPoints(idsPuntosPaletizado) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/ObtenerCantidadesProducidasPlanificacionesXPuntosPaletizado`, {
        params: {
          idsPuntosPaletizado,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callUpdateOriginDataOfAssignedBoxXSerialNumber(lstVolcadosXANS, idNumeroSerie) {
    const params = {
      idNumeroSerie,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Traceability}/Paletizado/UpdateOriginDataOfAssignedBoxXSerialNumber`, lstVolcadosXANS, {
        params,
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  callDeleteImportProducts(idsArticulo) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Traceability}/Paletizado/DeleteImportProducts`, {
        params: {
          idsArticulo,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  },

  /**
   * Fetches all active pallets for the given list of palletizing point ids.
   * @param {string} palletizingPointIds - A list of palletizing point ids separated by commas.
   */
  FetchActivePalletsXPalletizingPoint(palletizingPointIds) {
    return callExecuteSPERP('APPTRAZABILIDAD_PalesActivosXPuntosPaletizado', [
      {
        NombreParametro: 'IdsPuntosPaletizado',
        Valor: palletizingPointIds,
      },
    ], 'Trazabilidad.ElementoPaleEnProduccionJSON');
  },
};
